import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Calendar, Plus, X, Users, Clock, Link, Repeat, Share2 } from 'lucide-react'

import { mockMinisterios } from '@/mocks'

interface Membro {
  id: string
  nome: string
  email?: string
  telefone?: string
  cargo?: string
  foto?: string
}

interface Ministerio {
  id: string
  nome: string
  descricao?: string | null
  grupoFuncoes?: string
  ativo: boolean
}

interface MembroEquipe {
  membroId: string
  funcao: string
}

interface EscalaItem {
  id: string
  equipe: MembroEquipe[]
  data: string
  horario: string
  url?: string
  observacoes?: string
  recorrencia?: 'nenhuma' | 'primeiro-domingo' | 'segundo-domingo' | 'terceiro-domingo' | 'quarto-domingo' | 'ultimo-domingo'
}

interface EscalaMinisterioProps {
  ministerio: Ministerio
  membros: Membro[]
  escalasExistentes?: EscalaItem[]
  funcoesCadastradas?: string[]
  showForm?: boolean
  onToggleForm?: (show: boolean) => void
}

export function EscalaMinisterio({ 
  ministerio, 
  membros, 
  escalasExistentes = [], 
  funcoesCadastradas = [],
  showForm = false,
  onToggleForm
}: EscalaMinisterioProps) {
  const [escalas, setEscalas] = useState<EscalaItem[]>(escalasExistentes)
  const [novaEscala, setNovaEscala] = useState<Omit<EscalaItem, 'id'>>({
    equipe: [] as MembroEquipe[],
    data: '',
    horario: '',
    url: '',
    observacoes: '',
    recorrencia: 'nenhuma'
  })
  const [novoMembro, setNovoMembro] = useState({ membroId: '', funcao: '' })

  // Filtrar ministérios ativos excluindo o atual
  const ministeriosDisponiveis = mockMinisterios.filter(m => 
    m.ativo && m.id !== ministerio.id
  )

  // Função para compartilhar uma escala específica
  const handleCompartilharEscala = (escalaId: string, ministerioDestinoId: string) => {
    const escala = escalas.find(e => e.id === escalaId)
    const ministerioDestino = ministeriosDisponiveis.find(m => m.id === ministerioDestinoId)
    
    if (escala && ministerioDestino) {
      console.log(`Compartilhando escala ${escalaId} com ministério ${ministerioDestinoId}:`, escala)
      // Aqui seria implementada a lógica real de compartilhamento
      // Por exemplo: API call para compartilhar a escala
      alert(`Escala de ${escala.data} às ${escala.horario} compartilhada com sucesso com ${ministerioDestino.nome}!`)
    }
  }

  const handleAddMembroEquipe = () => {
    if (!novoMembro.membroId || !novoMembro.funcao) {
      return
    }

    // Verifica se o membro já está na equipe
    if (novaEscala.equipe.some(m => m.membroId === novoMembro.membroId)) {
      return
    }

    setNovaEscala(prev => ({
      ...prev,
      equipe: [...prev.equipe, { ...novoMembro }]
    }))
    setNovoMembro({ membroId: '', funcao: '' })
  }

  const handleRemoveMembroEquipe = (membroId: string) => {
    setNovaEscala(prev => ({
      ...prev,
      equipe: prev.equipe.filter(m => m.membroId !== membroId)
    }))
  }

  const handleAddEscala = () => {
    if (!novaEscala.data || !novaEscala.horario || novaEscala.equipe.length === 0) {
      return
    }

    const escala: EscalaItem = {
      id: Date.now().toString(),
      equipe: novaEscala.equipe,
      data: novaEscala.data,
      horario: novaEscala.horario,
      url: novaEscala.url,
      observacoes: novaEscala.observacoes,
      recorrencia: novaEscala.recorrencia
    }

    setEscalas([...escalas, escala])
    setNovaEscala({
      equipe: [],
      data: '',
      horario: '',
      url: '',
      observacoes: '',
      recorrencia: 'nenhuma' as const
    })
    setNovoMembro({ membroId: '', funcao: '' })
    onToggleForm?.(false)
  }

  const handleRemoveEscala = (id: string) => {
    setEscalas(escalas.filter(escala => escala.id !== id))
  }

  const getMembro = (membroId: string) => {
    return membros.find(membro => membro.id === membroId)
  }

  const getRecorrenciaLabel = (recorrencia: string) => {
    const labels = {
      'nenhuma': 'Apenas esta data',
      'primeiro-domingo': '1º domingo do mês',
      'segundo-domingo': '2º domingo do mês',
      'terceiro-domingo': '3º domingo do mês',
      'quarto-domingo': '4º domingo do mês',
      'ultimo-domingo': 'Último domingo do mês'
    }
    return labels[recorrencia as keyof typeof labels] || recorrencia
  }

  // Agrupar escalas por data
  const escalasAgrupadas = escalas.reduce((acc, escala) => {
    const data = escala.data
    if (!acc[data]) {
      acc[data] = []
    }
    acc[data].push(escala)
    return acc
  }, {} as Record<string, EscalaItem[]>)

  return (
    <div className="space-y-6">

      {/* Formulário de Nova Escalação */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Nova Escalação
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4">
              {/* Seção de Equipe */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Equipe da Escalação
                </h4>
                
                {/* Lista de membros da equipe */}
                {novaEscala.equipe.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {novaEscala.equipe.map((membro, index) => {
                      const membroInfo = getMembro(membro.membroId)
                      return (
                        <div key={index} className="flex items-center justify-between bg-muted p-2 rounded">
                          <div className="flex items-center gap-2">
                            <Avatar className="h-6 w-6">
                              <AvatarImage src={membroInfo?.foto} />
                              <AvatarFallback className="text-xs">
                                {membroInfo?.nome ? membroInfo.nome.split(' ').map(n => n[0]).join('').slice(0, 2) : '??'}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm font-medium">{membroInfo?.nome}</span>
                            <Badge variant="secondary" className="text-xs">{membro.funcao}</Badge>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveMembroEquipe(membro.membroId)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* Formulário para adicionar membro */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <div>
                    <Select value={novoMembro.membroId} onValueChange={(value) => setNovoMembro({...novoMembro, membroId: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um membro" />
                      </SelectTrigger>
                      <SelectContent>
                        {membros
                          .filter(membro => !novaEscala.equipe.some(e => e.membroId === membro.id))
                          .map((membro) => (
                            <SelectItem key={membro.id} value={membro.id}>
                              {membro.nome}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Select value={novoMembro.funcao} onValueChange={(value) => setNovoMembro({...novoMembro, funcao: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione uma função" />
                      </SelectTrigger>
                      <SelectContent>
                        {funcoesCadastradas.length > 0 ? (
                          funcoesCadastradas.map((funcao) => (
                            <SelectItem key={funcao} value={funcao}>
                              {funcao}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="sem-funcoes" disabled>
                            Nenhuma função cadastrada ainda
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddMembroEquipe}
                    disabled={!novoMembro.membroId || !novoMembro.funcao}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Adicionar
                  </Button>
                </div>
              </div>

              {/* Campos de data, horário, etc. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="data">Data</Label>
                  <Input
                    id="data"
                    type="date"
                    value={novaEscala.data}
                    onChange={(e) => setNovaEscala({...novaEscala, data: e.target.value})}
                  />
                </div>

                <div>
                  <Label htmlFor="horario">Horário</Label>
                  <Input
                    id="horario"
                    type="time"
                    value={novaEscala.horario}
                    onChange={(e) => setNovaEscala({...novaEscala, horario: e.target.value})}
                  />
                </div>

                <div>
                  <Label htmlFor="url">URL (opcional)</Label>
                  <Input
                    id="url"
                    type="url"
                    placeholder="https://exemplo.com"
                    value={novaEscala.url}
                    onChange={(e) => setNovaEscala({...novaEscala, url: e.target.value})}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Link para material, playlist ou documento relacionado
                  </p>
                </div>

                <div>
                  <Label htmlFor="recorrencia">Recorrência</Label>
                  <Select value={novaEscala.recorrencia ?? 'nenhuma'} onValueChange={(value) => setNovaEscala({...novaEscala, recorrencia: value as EscalaItem['recorrencia']})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nenhuma">Apenas esta data</SelectItem>
                      <SelectItem value="primeiro-domingo">1º domingo do mês</SelectItem>
                      <SelectItem value="segundo-domingo">2º domingo do mês</SelectItem>
                      <SelectItem value="terceiro-domingo">3º domingo do mês</SelectItem>
                      <SelectItem value="quarto-domingo">4º domingo do mês</SelectItem>
                      <SelectItem value="ultimo-domingo">Último domingo do mês</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="observacoes">Observações (opcional)</Label>
                <Input
                  id="observacoes"
                  placeholder="Observações adicionais"
                  value={novaEscala.observacoes}
                  onChange={(e) => setNovaEscala({...novaEscala, observacoes: e.target.value})}
                />
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button onClick={handleAddEscala} disabled={!novaEscala.data || !novaEscala.horario || novaEscala.equipe.length === 0}>
                Adicionar Escalação
              </Button>
              <Button variant="outline" onClick={() => onToggleForm?.(false)}>
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista de Escalações */}
      <div className="space-y-4">
        {Object.keys(escalasAgrupadas).length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-8">
              <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Nenhuma escalação cadastrada</h3>
              <p className="text-muted-foreground text-center mb-4">
                Comece criando uma nova escalação para o ministério
              </p>
              <Button onClick={() => onToggleForm?.(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Criar primeira escalação
              </Button>
            </CardContent>
          </Card>
        ) : (
          Object.entries(escalasAgrupadas)
            .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
            .map(([data, escalasData]) => (
              <Card key={data}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    {new Date(data + 'T00:00:00').toLocaleDateString('pt-BR', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {escalasData.map((escala) => (
                      <div key={escala.id} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{escala.horario}</span>
                            {escala.recorrencia !== 'nenhuma' && (
                              <Badge variant="outline" className="flex items-center gap-1">
                                <Repeat className="h-3 w-3" />
                                {getRecorrenciaLabel(escala.recorrencia || 'nenhuma')}
                              </Badge>
                            )}
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">Playlist:</span>
                              {escala.url ? (
                                <a
                                  href={escala.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                >
                                  <Link className="h-4 w-4" />
                                  <span className="text-sm">Link disponível</span>
                                </a>
                              ) : (
                                <span className="text-sm text-muted-foreground">N/A</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Dropdown de compartilhamento para esta escala */}
                            <Select onValueChange={(ministerioId) => handleCompartilharEscala(escala.id, ministerioId)}>
                              <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="Compartilhar com..." />
                              </SelectTrigger>
                              <SelectContent>
                                {ministeriosDisponiveis.map((m) => (
                                  <SelectItem key={m.id} value={m.id}>
                                    <div className="flex items-center gap-2">
                                      <Share2 className="h-3 w-3" />
                                      {m.nome}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveEscala(escala.id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Equipe */}
                        <div className="space-y-2">
                          <h5 className="text-sm font-medium text-muted-foreground">Equipe:</h5>
                          <div className="flex flex-wrap gap-2">
                            {escala.equipe.map((membroEquipe, index) => {
                              const membro = getMembro(membroEquipe.membroId)
                              return (
                                <div key={index} className="flex items-center gap-2 bg-muted p-2 rounded">
                                  <Avatar className="h-6 w-6">
                                    <AvatarImage src={membro?.foto} />
                                    <AvatarFallback className="text-xs">
                                      {membro?.nome ? membro.nome.split(' ').map(n => n[0]).join('').slice(0, 2) : '??'}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div className="text-sm">
                                     <div className="font-medium">{membro?.nome || 'Membro não encontrado'}</div>
                                     <div className="text-muted-foreground">{membroEquipe.funcao}</div>
                                   </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>

                        {escala.observacoes && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-sm text-muted-foreground">
                              <strong>Observações:</strong> {escala.observacoes}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))
        )}
      </div>
    </div>
  )
}